import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, CalendarDays } from "lucide-react";
import MarketingShell from "../components/MarketingShell";
import { useAuth } from "../lib/auth";

// iter-139 — Resources page, mirrors steeltooth.ca/resources/
// (image 5 from the user's Sep 3 2026 message). Titles + dates +
// excerpts are lifted verbatim; article bodies are stubbed to a
// friendly "coming soon" until the user provides the full copy.

const ARTICLES = [
    {
        slug: "rejuvenation-instead-of-replacement",
        title: "Rejuvenation instead of Replacement",
        date: "January 9, 2025",
        excerpt: "Rejuvenating a septic system can be a better option than replacing it in many cases for several reasons. Here are some key factors to consider:",
        cat: "Septic",
    },
    {
        slug: "why-hire-a-professional-contractor",
        title: "Why Hire a Professional Contractor?",
        date: "September 25, 2024",
        excerpt: "It might seem obvious, but here are a few reasons why hiring a professional can save you time, money, and potentially a lot of stress!",
        cat: "Advice",
    },
    {
        slug: "understanding-residential-demolition",
        title: "Understanding Residential Demolition: A Guide by Steeltooth Contracting",
        date: "July 26, 2024",
        excerpt: "When it comes to renovating or rebuilding your home, understanding the type of demolition needed is crucial. At Steeltooth Contracting, we specialize in a range of demolition services.",
        cat: "Demolition",
    },
    {
        slug: "6-signs-time-for-septic-inspection",
        title: "6 Signs It's Time for a Septic System Inspection",
        date: "May 30, 2024",
        excerpt: "Maintaining a healthy septic system is crucial for any homeowner. Neglecting it can lead to significant problems, which often means costly repairs. Regular inspections are…",
        cat: "Septic",
        img: "/images/services/septic.jpg",
    },
    {
        slug: "demolition-services-planning-execution",
        title: "Demolition Services: Planning, Execution, and Future Trends",
        date: "May 21, 2024",
        excerpt: "Effective demolition services begin with thorough planning and preparation. Contractors conduct site evaluations to identify potential hazards such as asbestos or unstable structures. This assessment…",
        cat: "Demolition",
        img: "/images/services/demolition.jpg",
    },
    {
        slug: "excavation-contractors-digging-deep",
        title: "Excavation Contractors: The Essential Work of Digging Deep",
        date: "May 15, 2024",
        excerpt: "Excavation involves more than just digging dirt; it's a crucial component in construction and development. Excavation contractors prepare sites for construction, ensuring the ground is…",
        cat: "Excavation",
        img: "/images/services/excavation.jpg",
    },
];

export default function ResourcesPage() {
    const navigate = useNavigate();
    const { user } = useAuth();
    useEffect(() => { /* iter-166 — auto-redirect removed; user can browse freely */ }, [user, navigate]);

    return (
        <MarketingShell testid="resources-page">
            <section
                className="hero-dark relative h-[42vh] min-h-[300px] flex items-end text-white"
                data-testid="resources-hero"
            >
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: "url(/images/services/site.jpg)" }}
                    aria-hidden="true"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-slate-900/50 to-slate-900/80" aria-hidden="true" />
                <div className="relative z-10 max-w-6xl mx-auto w-full px-4 sm:px-8 pb-10">
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Resources</h1>
                    <p className="mt-3 text-slate-200 max-w-xl">
                        Field-tested guides from the Steeltooth crew — septic, excavation, demolition, and every dirty job in between.
                    </p>
                </div>
            </section>

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14 space-y-6">
                {ARTICLES.map((a) => (
                    <article
                        key={a.slug}
                        className="rounded-lg overflow-hidden bg-white border border-slate-200 hover:border-[#00A3FF] hover:shadow-md transition-all"
                        data-testid={`resource-${a.slug}`}
                    >
                        {a.img && (
                            <div className="h-56 bg-slate-200">
                                <img
                                    src={a.img}
                                    alt={a.title}
                                    loading="lazy"
                                    className="w-full h-full object-cover"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                />
                            </div>
                        )}
                        <div className="p-5 sm:p-6">
                            <div className="text-[11px] uppercase tracking-[0.15em] text-[#00A3FF] font-bold">{a.cat}</div>
                            <h2 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900">
                                <Link to={`/resources/${a.slug}`} className="hover:text-[#007AFF]">
                                    {a.title}
                                </Link>
                            </h2>
                            <p className="mt-2 text-sm text-slate-600 leading-relaxed">{a.excerpt}</p>
                            <div className="mt-4 flex items-center justify-between">
                                <div className="flex items-center gap-1.5 text-xs text-slate-500">
                                    <CalendarDays className="w-3.5 h-3.5" />
                                    {a.date}
                                </div>
                                <Link
                                    to={`/resources/${a.slug}`}
                                    className="inline-flex items-center gap-1 text-sm font-bold text-[#007AFF] hover:text-[#0369A1]"
                                    data-testid={`resource-read-${a.slug}`}
                                >
                                    Read more <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    </article>
                ))}
            </div>
        </MarketingShell>
    );
}
